import { styled } from '@mui/joy/styles';
import { type ReactNode, MouseEvent } from 'react';

interface TagProps {
  label: string;
  endDecorator?: ReactNode;
  onTagClick?: () => void;
  onIconClick?: () => void;
}

/**
 * Tag component
 * @param param0
 * @param param0.label The text to display in the tag
 * @param param0.endDecorator The decorator to display at the end of the tag
 * @param param0.onTagClick The function to call when the tag is clicked
 * @param param0.onIconClick The function to call when the icon is clicked
 */
const Tag = ({ label, endDecorator, onTagClick, onIconClick }: TagProps) => {
  const isClickable = !!endDecorator;

  const handleIconClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    onIconClick?.();
  };

  return (
    <TagWrapper
      isClickable={isClickable}
      onClick={(event: MouseEvent<HTMLElement>) => {
        // Prevent parent click handlers (e.g. Autocomplete root) from firing
        event.stopPropagation();
        onTagClick?.();
      }}
    >
      <TagText>{label}</TagText>
      {endDecorator && (
        <TagIconWrapper className="tag-icon-wrapper" onClick={handleIconClick}>
          {endDecorator}
        </TagIconWrapper>
      )}
    </TagWrapper>
  );
};

export default Tag;

interface TagWrapperProps {
  isClickable?: boolean;
}

const TagWrapper = styled('div')<TagWrapperProps>(({ isClickable, theme }) => ({
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '8px 12px',
  borderRadius: '20px',
  border: `1px solid ${theme.palette.grey2}`,
  boxSizing: 'border-box',
  textOverflow: 'ellipsis',

  color: theme.palette.bluePrimary,
  fill: theme.palette.grey5,
  backgroundColor: theme.palette.white,
  cursor: isClickable ? 'pointer' : 'default',
  overflow: 'hidden',

  ...(isClickable && {
    '&:hover': {
      border: `1px solid ${theme.palette.grey2}`,
      // Show the icon only on hover
      '& .tag-icon-wrapper': {
        display: 'flex',
      },
    },
  }),
}));

const TagText = styled('span')({
  fontFamily: 'var(--joy-fontFamily-display)',
  fontSize: '14px',
  lineHeight: '20px',
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontWeight: 400,
  flex: 1,
});

const TagIconWrapper = styled('div')(({ theme }) => ({
  width: '16px',
  height: '16px',
  display: 'none',
  alignItems: 'center',
  color: theme.palette.grey4,
  fill: theme.palette.grey4,
  '& svg': {
    height: '16px',
    width: '16px',
  },
}));
